import { useBrand } from "@fprint/ui";
import { Link } from "react-router-dom";
import { businessIdentity } from "../lib/legal";

/**
 * Site-wide footer for the customer SPA. Surfaces the legal pages (Terms,
 * Privacy, Refunds/Returns, Contact), the support address, and the registered
 * business identity so a card-taking shop meets its basic disclosure duties.
 *
 * The business identity is operator-supplied (see brand/theme.json → business);
 * until it's filled in, a visible placeholder makes the gap obvious.
 */
const LEGAL_LINKS: { to: string; label: string }[] = [
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
  { to: "/refunds", label: "Refunds & Returns" },
  { to: "/contact", label: "Contact" },
];

export function SiteFooter() {
  const brand = useBrand();
  const { legalName, address, vatNumber } = businessIdentity;
  const identityParts = [
    legalName,
    address,
    vatNumber && `VAT ${vatNumber}`,
  ].filter(Boolean);

  return (
    <footer className="mx-auto w-full max-w-4xl px-6 py-8 text-center text-xs text-muted-fg">
      <nav
        aria-label="Legal and policies"
        className="mb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
      >
        {LEGAL_LINKS.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="underline hover:text-fg"
            data-testid={`footer-link-${link.to.slice(1)}`}
          >
            {link.label}
          </Link>
        ))}
      </nav>

      <p>
        {brand.name} — questions or problems with an order?{" "}
        <a
          href={`mailto:${brand.supportEmail}`}
          className="underline hover:text-fg"
          data-testid="support-email"
        >
          {brand.supportEmail}
        </a>
      </p>

      <p className="mt-3">
        {identityParts.length > 0 ? (
          <span data-testid="business-identity">{identityParts.join(" · ")}</span>
        ) : (
          <span data-testid="business-identity-placeholder">
            [ Registered business name · address · VAT number — operator must
            supply before go-live ]
          </span>
        )}
      </p>
    </footer>
  );
}
